import { MouseEvent } from "react";
import { Box, IconButton, Tooltip } from "@material-ui/core";
import { CrmHotFireIcon } from "../../contacts/crm/CrmIcons";

/**
 * The 🔥 toggle — one tap, no menu, no confirmation.
 *
 * Deliberately a separate control from the status chip rather than a badge on the
 * avatar: a badge does not read as clickable, and merging it into the status chip would
 * cost a menu open for what is a binary the realtor flips constantly.
 *
 * Measured caveat worth knowing: only 80 of 47,872 pilot contacts are flagged hot
 * (0.17%). So the OFF state is what this renders almost always, and it must still look
 * like a control rather than a decoration — hence a visible outline flame, not a blank.
 */
export default function CrmHotToggle({
  isHot,
  onToggle,
  size = 34,
  readOnly = false
}: {
  isHot: boolean;
  onToggle: () => void;
  size?: number;
  /**
   * The viewer may not write this contact — a colleague's row on the agency tab. See
   * `crmRowAccess`.
   *
   * A HOT contact still shows its flame, because that is information about the lead and
   * the agency tab exists to read it. An unflagged one renders NOTHING: the off state is
   * drawn as a visible outline flame precisely so it reads as a control, and a control
   * that cannot be operated is worse than an absence — it invites the tap and then eats
   * it. `disabled` was the other option and is wrong for the same reason at 0.17%
   * flagged: a greyed flame on essentially every row of somebody else's book is a
   * permanent apology nobody asked for.
   */
  readOnly?: boolean;
}) {
  const handle = (event: MouseEvent) => {
    // The row opens the contact; this must not.
    event.stopPropagation();
    onToggle();
  };

  if (readOnly) {
    if (!isHot) return null;
    return (
      <Tooltip title="ליד חם">
        {/* A Box, not a disabled IconButton: no ripple, no focus ring, nothing in the tab
            order. It is a status glyph now, and it should measure like one — the same box
            the button occupied, so a read-only row still lines up with an editable one. */}
        <Box
          /* `role="img"` is what makes the label COUNT. `aria-label` only names elements
             whose role supports a name, and a bare div's does not — so without this the
             one thing the read-only branch exists to preserve, the flame a manager scans
             the agency tab for, is announced as nothing at all, while the identical flame
             on the viewer's own row (an IconButton) is announced normally. The Tooltip
             does not rescue it: MUI sets `aria-describedby`, which is a description, not
             a name. `role` costs no pixels, which the geometry note below depends on. */
          role="img"
          aria-label="ליד חם"
          sx={{
            width: size,
            height: size,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "error.main"
          }}
        >
          <CrmHotFireIcon isHot sx={{ fontSize: size >= 44 ? 22 : 18 }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={isHot ? "בטל סימון כליד חם" : "סמן כליד חם"}>
      <IconButton
        onClick={handle}
        aria-label={isHot ? "ליד חם" : "סמן כליד חם"}
        aria-pressed={isHot}
        sx={{
          width: size,
          height: size,
          color: isHot ? "error.main" : "text.disabled",
          "&:hover": { color: isHot ? "error.dark" : "error.light" }
        }}
      >
        {/* The glyph tracks the button. At the 44px touch size the mobile card asks for,
            a fixed 18px flame left a 13px ring of dead-looking padding and the control
            read as an icon that had failed to load. */}
        <CrmHotFireIcon isHot={isHot} sx={{ fontSize: size >= 44 ? 22 : 18 }} />
      </IconButton>
    </Tooltip>
  );
}
